import React, { useState, useEffect, useRef } from "react";
import * as ReactDOMServer from "react-dom/server";
import DeckGL from "@deck.gl/react";
import { useQuery } from "@tanstack/react-query";
import { MapView, FlyToInterpolator, COORDINATE_SYSTEM } from "@deck.gl/core";
import { TileLayer, MVTLayer } from "@deck.gl/geo-layers";
import { BitmapLayer, PathLayer } from "@deck.gl/layers";

import {
  calcDateDuration,
  stringToNumber,
  hexToRgb,
  formatUtcDate,
} from "../utils";
import {
  calculateLineWidth,
  calculateEncompassingBbox,
  calculateZoomedViewport,
  makoTileSpecToInitialViewState,
  getTimestampForSelection,
  chunkArray,
} from "../utils/mapUtil";

import DeploymentInfoPanel from "./deploymentInfoPanel";
import tailwindTheme from "@/styles/tailwind.theme";
import { getUpstreamEndpoint } from "../apiUtils";
import StickyEventTooltip from "./stickyEventTooltip";
import { getCartoVoyagerRasterTileUrls } from "@/utils/cartoBasemaps";

const tailwindConfig = tailwindTheme;

const pickColorForId = (id: any, opacity = 100) => {
  const colors = [...Object.values(tailwindConfig.theme.colors.mapColorRamp)];
  return [...hexToRgb(colors[id % colors.length]), opacity];
};

const MAX_CHUNK_SIZE = 200;
const SELECTED_DEPLOYMENT_FLYTO_DURATION = 3000;
const PINNED_EVENT_FLYTO_DURATION = 800;

const LINK_STYLE = "color-black opacity-75 cursor-grab";

const devicePixelRatio =
  (typeof window !== "undefined" && window.devicePixelRatio) || 1;

function isValidBbox(bbox: number[]) {
  return !bbox.every((val: number) => val === Infinity || val === -Infinity);
}

const getDeploymentTracklineTooltip = ({ object, coordinate = null }: { object: any; coordinate?: any }) => {
  const dt = getTimestampForSelection(object, coordinate);

  return {
    html: ReactDOMServer.renderToString(
      <>
        <p>Name: {object.properties.deployment_name}</p>
        <p>External ID: {object.properties.external_id}</p>
        <p>Date: {dt}</p>
        Deployment Duration:{" "}
        {calcDateDuration(
          object.properties.deployment_start_dt,
          object.properties.deployment_end_dt
        )}
        <hr className={"my-2"}></hr>
        <p className={"line-clamp-3"}>
          {object.properties.deployment_description}
        </p>
      </>
    ),
  };
};

const getSealogEventHoverTooltip = ({ object, deployments = [] }: { object: any; deployments?: any[] }) => {
  const deployment = deployments[0];

  const timeFromStart = deployment?.deployment_start_dt
    ? calcDateDuration(
        deployment.deployment_start_dt,
        object.properties.ts,
        false
      )
    : "N/A";

  return {
    html: ReactDOMServer.renderToString(
      <>
        <p className="text-md font-semibold leading-6 text-blue-300">
          {object.properties.event_value}
        </p>
        <p className="text-sm">
          <strong>Time:</strong> {formatUtcDate(object.properties.ts)}
        </p>
        <p className="text-sm">
          <strong>Time From Start:</strong> {timeFromStart}
        </p>
        <p className="text-sm">
          <strong>Description:</strong> {object.properties.event_free_text}
        </p>
        <hr className={"my-2"}></hr>
        <p className="line-clamp-3 text-sm">Click for more details!</p>
      </>
    ),
  };
};

function getTooltip({ object, ...args }: { object: any; [key: string]: any }, hasStickyTooltip: any, deployments: any) {
  if (hasStickyTooltip) {
    return null;
  }
  if (!object?.properties?.layerName) {
    return null;
  }

  switch (object.properties.layerName) {
    case "public.deployment_trackline":
      return getDeploymentTracklineTooltip({ object, ...args });
    case "public.sealog_event_geography":
      return getSealogEventHoverTooltip({ object, deployments, ...args });
    default:
      throw new Error(`Unexpected layer: ${object.properties.layerName}`);
  }
}

function createSealogEventLayer({
  id,
  filterClause,
  hoveredEventId,
  selectedEventId,
  deckRef,
  timelineItemsById,
  onEventSelect,
  onEventHover,
  setViewport,
  setSelectedEventTooltip,
}: {
  id: any;
  filterClause: any;
  hoveredEventId: any;
  selectedEventId: any;
  deckRef: any;
  timelineItemsById: any;
  onEventSelect: any;
  onEventHover: any;
  setViewport: any;
  setSelectedEventTooltip: any;
}) {
  return new MVTLayer({
    id,
    data: `${getUpstreamEndpoint()}/tile/public.sealog_event_geography/{z}/{x}/{y}.pbf?filter=${encodeURIComponent(
      filterClause
    )}`,
    coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
    minZoom: 0,
    maxZoom: 22,
    loadOptions: {
      fetch: {
        method: "GET",
        credentials: "include",
      },
    },
    getFillColor: hexToRgb(tailwindConfig.theme.colors.orange),
    lineWidthMinPixels: 1,
    parameters: {
      depthTest: false,
    },
    getLineWidth: 2,
    lineWidthUnits: "pixels",
    pointRadiusMinPixels: 6,
    pointRadiusMaxPixels: 10,
    getPointRadius: ({ properties }: { properties: any }) => {
      const isHovered = properties.public_id === hoveredEventId;
      const isSelected = properties.public_id === selectedEventId;
      return isHovered || isSelected ? 10 : 8;
    },
    radiusUnits: "pixels",
    getLineColor: ({ properties }: { properties: any }) => {
      const isHovered = properties.public_id === hoveredEventId;
      const isSelected = properties.public_id === selectedEventId;
      return isHovered || isSelected ? [0, 0, 0] : [0, 0, 0, 0];
    },
    updateTriggers: {
      getLineColor: [hoveredEventId, selectedEventId],
      getFillColor: [hoveredEventId, selectedEventId],
      getPointRadius: [hoveredEventId, selectedEventId],
    },
    pickable: true,
    onHover: ({ object }: { object: any }) => {
      if (!onEventHover) return;
      if (object?.properties?.public_id) {
        onEventHover(object.properties.public_id);
      } else {
        onEventHover(null);
      }
    },
    onClick: ({ object, coordinate }: { object: any; coordinate: any }) => {
      if (!object?.properties?.public_id) {
        setSelectedEventTooltip(null);
        return;
      }
      const publicId = object.properties.public_id;
      onEventSelect?.(publicId);
      setViewport?.((prev: any) => ({
        ...prev,
        longitude: coordinate[0],
        latitude: coordinate[1],
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: PINNED_EVENT_FLYTO_DURATION,
      }));
      const timelineItem = timelineItemsById?.[publicId];
      const eventOptions = timelineItem?.rawEvent?.event_options || [];
      setSelectedEventTooltip?.({
        longitude: coordinate[0],
        latitude: coordinate[1],
        eventType: timelineItem?.title,
        eventTimestamp: timelineItem?.date,
        freeText: timelineItem?.description,
        depth: timelineItem?.depth,
        eventOptions,
        timeFromStart: timelineItem?.timeFromStart,
      });
      // The logic below was added so the hover tooltip vanishes on click, even if the user doesn't move their mouse away
      const deck = deckRef?.current?.deck;
      if (deck) {
        const canvas = deck.canvas;
        if (canvas) {
          const evt = new PointerEvent("mouseleave", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          canvas.dispatchEvent(evt);
        }
      }
    },
  });
}

export default function Map({
  showBorder = false,
  onTilesLoad = null,
  makoTileLayer = "public.deployment_trackline",
  deployments,
  currentPageProp,
  pageSizeProp,
  selectedDeploymentIds: propSelectedDeploymentIds = [],
  showInfoPanel = true,
  selectedEventId = null,
  hoveredEventId = null,
  mapLocation,
  pinnedEventDetail,
  timelineItemsById,
  onEventSelect,
  onEventHover,
  filtersApplied = false,
  allFilteredEventIds = new Set(),
  onEventDeselect,
}: {
  showBorder?: boolean;
  onTilesLoad?: any;
  makoTileLayer?: string;
  deployments: any;
  currentPageProp: any;
  pageSizeProp: any;
  selectedDeploymentIds?: any[];
  showInfoPanel?: boolean;
  selectedEventId?: any;
  hoveredEventId?: any;
  mapLocation: any;
  pinnedEventDetail: any;
  timelineItemsById: any;
  onEventSelect: any;
  onEventHover: any;
  filtersApplied?: boolean;
  allFilteredEventIds?: Set<any>;
  onEventDeselect: any;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<any>(null);

  const [selectedDeploymentIds, setSelectedDeploymentIds] = useState(
    deployments.map((deployment: any) => deployment.external_id)
  );

  const [focusedDeploymentId, setFocusedDeploymentId] = useState<any>(null);
  const [viewport, setViewport] = useState<any>(null);
  const [shouldZoomIn, setShouldZoomIn] = useState(false);
  const [selectedEventTooltip, setSelectedEventTooltip] = useState<any>(null);

  const handleSelectedDeploymentsChange = (deploymentIds: any) => {
    setSelectedDeploymentIds(deploymentIds);
    setShouldZoomIn(true);
  };

  const handleFocusDeployment = (deploymentId: any) => {
    setFocusedDeploymentId(deploymentId);
    handleSelectedDeploymentsChange([deploymentId]);
  };

  const handleStickyTooltipClose = () => {
    setSelectedEventTooltip(null);
    if (onEventDeselect) {
      onEventDeselect();
    }
  };

  const { isLoading, data: makoTileLayerSpec } = useQuery({
    queryKey: ["getMakoTileLayerSpec"],
    queryFn: () =>
      fetch(`${getUpstreamEndpoint()}/tile/${makoTileLayer}.json`, {
        credentials: "include",
      }).then((resp) => resp.json()),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!selectedEventId) {
      setSelectedEventTooltip(null);
    }
  }, [selectedEventId]);

  // Set initial zoomed-out viewport
  useEffect(() => {
    if (!isLoading && makoTileLayerSpec && containerRef.current && !viewport) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      const initialViewport = makoTileSpecToInitialViewState({
        ...makoTileLayerSpec,
        width,
        height,
      });
      setViewport(initialViewport);

      // Schedule zoom-in after a short delay
      setTimeout(() => setShouldZoomIn(true), 1000);
    }
  }, [isLoading, makoTileLayerSpec, viewport]);

  // Zoom to selected deployments
  useEffect(() => {
    if (
      shouldZoomIn &&
      !isLoading &&
      selectedDeploymentIds.length > 0 &&
      containerRef.current
    ) {
      const encompassingBbox = calculateEncompassingBbox(
        selectedDeploymentIds,
        deployments
      );
      if (isValidBbox(encompassingBbox)) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        const zoomedViewport = calculateZoomedViewport(
          encompassingBbox,
          width,
          height
        );
        setViewport({
          ...zoomedViewport,
          transitionInterpolator: new FlyToInterpolator(),
          transitionDuration: SELECTED_DEPLOYMENT_FLYTO_DURATION,
        });
      }
      setShouldZoomIn(false);
    }
  }, [shouldZoomIn, isLoading, selectedDeploymentIds, deployments]);

  // If user selects event in timeline => update map
  useEffect(() => {
    if (mapLocation?.lon !== undefined && mapLocation?.lat !== undefined) {
      setViewport((prev: any) => ({
        ...prev,
        longitude: mapLocation.lon,
        latitude: mapLocation.lat,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: PINNED_EVENT_FLYTO_DURATION,
      }));
    }
  }, [mapLocation]);

  // If pinned event detail changes => set new sticky tooltip
  useEffect(() => {
    if (pinnedEventDetail && mapLocation) {
      setSelectedEventTooltip({
        longitude: mapLocation.lon,
        latitude: mapLocation.lat,
        eventType: pinnedEventDetail.eventType,
        eventTimestamp: pinnedEventDetail.eventTimestamp,
        freeText: pinnedEventDetail.freeText,
        depth: pinnedEventDetail.depth,
        eventOptions: pinnedEventDetail.eventOptions,
        timeFromStart: pinnedEventDetail.timeFromStart,
      });
    } else {
      setSelectedEventTooltip(null);
    }
  }, [pinnedEventDetail, mapLocation]);

  let filter;
  if (selectedDeploymentIds.length > 0) {
    const encodedIds = selectedDeploymentIds
      .map((id: any) => `'${encodeURIComponent(id)}'`)
      .join(",");
    filter = `is_primary=true AND external_id IN (${encodedIds})`;
  } else {
    filter = "1=0";
  }

  const onDeploymentTileLoad = (tile: any) => {
    // We pass timestamp data as an array from the backend, but
    // the MVT spec does not provide direct support for deserializing arrays, so the array is presented as a single
    // serialized string. We deserialize the string here, so that we can index into the array when rendering tooltips.
    const parseJson = (p: any) => {
      p.timestamps = JSON.parse(p.timestamps);
      p.lat = JSON.parse(p.lat);
      p.lng = JSON.parse(p.lng);
    };

    tile.content.lines.properties.map(parseJson);
    tile.content.points.properties.map(parseJson);
  };

  const osmTileLayer = new TileLayer({
    data: getCartoVoyagerRasterTileUrls(),
    maxRequests: 20,
    pickable: false,
    onViewportLoad: onTilesLoad,
    autoHighlight: showBorder,
    highlightColor: [60, 60, 60, 40],
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    zoomOffset: devicePixelRatio === 1 ? -1 : 0,
    renderSubLayers: (props: any) => {
      const {
        bbox: { west, south, east, north },
      } = props.tile;

      return [
        new BitmapLayer(props, {
          data: null,
          image: props.data,
          bounds: [west, south, east, north],
        }),
        showBorder &&
          new PathLayer({
            id: `${props.id}-border`,
            data: [
              [
                [west, north],
                [west, south],
                [east, south],
                [east, north],
                [west, north],
              ],
            ],
            getPath: (d: any) => d,
            getColor: [255, 0, 0],
            widthMinPixels: 4,
          }),
      ];
    },
  });

  const realDeploymentTileLayer = new MVTLayer({
    data: `${getUpstreamEndpoint()}/tile/public.deployment_trackline/{z}/{x}/{y}.pbf?filter=${filter}`,
    updateTriggers: {
      data: [filter],
      getLineWidth: [viewport?.zoom],
    },
    minZoom: 0,
    getFillColor: hexToRgb(tailwindConfig.theme.colors.black),
    getLineColor: ({ properties }: { properties: any }) => {
      const { external_id: deploymentId } = properties;
      const numericId = stringToNumber(deploymentId);
      return pickColorForId(numericId, 75);
    },
    getLineWidth: ({ properties }: { properties: any }) => {
      const numOfCoordinates = properties.lat.length;
      return calculateLineWidth(
        viewport?.zoom,
        numOfCoordinates,
        properties.asset_type
      );
    },
    lineWidthMinPixels: 1,
    lineWidthUnits: "pixels",
    lineCapRounded: true,
    lineJointRounded: true,
    onViewportLoad: onTilesLoad,
    onTileLoad: onDeploymentTileLoad,
    loadOptions: {
      fetch: {
        method: "GET",
        credentials: "include",
      },
    },
    pickable: true,
    onClick: (info: any) => {
      if (info?.object?.properties) {
        const deploymentId = info.object.properties.external_id;
        handleFocusDeployment(deploymentId);
      }
    },
    autoHighlight: true,
    uniqueIdProperty: "external_id",
    highlightColor: hexToRgb(tailwindConfig.theme.colors.orange),
  });

  // Turn the set of IDs into chunked arrays:
  let sealogEventLayers = [];

  if (!filtersApplied) {
    const finalIds = new Set();
    if (selectedEventId) finalIds.add(selectedEventId);
    if (hoveredEventId) finalIds.add(hoveredEventId);

    if (finalIds.size > 0) {
      const filterClause = `public_id IN ('${[...finalIds].join("','")}')`;
      sealogEventLayers.push(
        createSealogEventLayer({
          id: "sealog-event-layer-unfiltered-by-user",
          filterClause,
          hoveredEventId,
          selectedEventId,
          deckRef,
          timelineItemsById,
          onEventSelect,
          onEventHover,
          setViewport,
          setSelectedEventTooltip,
        })
      );
    }
  } else {
    // filters are applied and chunked to avoid huge filter strings
    const allIdsArray = Array.from(allFilteredEventIds);
    const chunks = chunkArray(allIdsArray, MAX_CHUNK_SIZE);

    chunks.forEach((chunk, chunkIndex) => {
      const filterClause = `public_id IN ('${chunk.join("','")}')`;
      sealogEventLayers.push(
        createSealogEventLayer({
          id: `sealog-event-layer-chunk-${chunkIndex}`,
          filterClause,
          hoveredEventId,
          selectedEventId,
          deckRef,
          timelineItemsById,
          onEventSelect,
          onEventHover,
          setViewport,
          setSelectedEventTooltip,
        })
      );
    });
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        zIndex: 20,
      }}
    >
      {!viewport ? (
        "Loading..."
      ) : (
        <>
          {showInfoPanel && (
            <DeploymentInfoPanel
              deployments={deployments}
              focusedDeploymentId={focusedDeploymentId}
              onSelectedDeploymentChange={handleSelectedDeploymentsChange}
              currentPageProp={currentPageProp}
              pageSizeProp={pageSizeProp}
            />
          )}
          <DeckGL
            ref={deckRef}
            className="absolute left-0 top-0 z-0 h-full w-full"
            layers={[
              osmTileLayer,
              realDeploymentTileLayer,
              ...sealogEventLayers,
            ]}
            views={new MapView({ repeat: true })}
            viewState={viewport}
            onViewStateChange={({ viewState, interactionState }: { viewState: any; interactionState: any }) => {
              // The code below is a bandaid fix for the map not being zoomable upon initial load on the RA Detail page
              // TODO: investigate the real cause of this bug, and change this onViewStateChange to just setViewport(viewState) again
              if (interactionState.isZooming || interactionState.isPanning) {
                delete viewState.transitionDuration;
                delete viewState.transitionInterpolator;
              }
              setViewport(viewState);
            }}
            controller={true}
            getTooltip={(info: any) =>
              getTooltip(info, !!selectedEventTooltip, deployments)
            }
            getCursor={({ isHovering }: { isHovering: any }) => (isHovering ? "pointer" : "grab")}
          >
            <div className="absolute bottom-0 right-0 bg-white bg-opacity-50 px-3 py-0 text-xs">
              {"© "}
              <a
                className={LINK_STYLE}
                href="http://www.openstreetmap.org/copyright"
              >
                OpenStreetMap
              </a>
              {" © "}
              <a className={LINK_STYLE} href="https://carto.com/attributions">
                CARTO
              </a>
            </div>
          </DeckGL>

          {selectedEventTooltip && (
            <StickyEventTooltip
              viewport={viewport}
              tooltip={selectedEventTooltip}
              onClose={handleStickyTooltipClose}
            />
          )}
        </>
      )}
    </div>
  );
}
